import { useState } from 'react'
import { Download, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Link } from '@tanstack/react-router'

import { DebtTypeBadge } from './debt-type-badge'
import { CustomerDebtPaymentDialog } from './customer-debt-payment-dialog'
import { CustomerDebtAdjustmentDialog } from './customer-debt-adjustment-dialog'
import { CustomerRefundDialog } from './customer-refund-dialog'
import { CustomerPaymentReceiptDialog } from './customer-payment-receipt-dialog'
import type {
    DebtTransaction,
    DebtTransactionType,
} from '@/lib/api/customer-debt-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { customerDebtApi } from '@/lib/api/customer-debt-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useCustomerDebtTransactions,
    useDeleteDebtTransaction,
} from '@/hooks/use-customer-debt-query'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/use-auth'
import { formatDocCode, formatVN, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const TYPES: DebtTransactionType[] = [
    'sale',
    'payment',
    'adjustment',
    'return',
    'opening',
    'refund',
    'return_fee',
]

const TYPE_LABEL: Record<DebtTransactionType, () => string> = {
    sale: () => m.customer_debt_type_sale(),
    payment: () => m.customer_debt_type_payment(),
    adjustment: () => m.customer_debt_type_adjustment(),
    return: () => m.customer_debt_type_return(),
    opening: () => m.customer_debt_type_opening(),
    refund: () => m.customer_debt_type_refund(),
    return_fee: () => m.customer_debt_type_return_fee(),
}

const PER_PAGE = 20

// Chỉ dòng nhân viên tạo tay mới có nút xóa: điều chỉnh + thanh toán. Server
// vẫn là chốt chặn cuối (COD/hệ thống/không truy vết được → 409); FE chỉ lọc
// thô theo type để không mời bấm vào dòng chắc chắn bị chặn (sale/return/opening).
function isDeletableType(type: DebtTransactionType): boolean {
    return type === 'adjustment' || type === 'payment'
}

export function DebtLedgerTab({
    customerId,
    currentDebt,
}: {
    customerId: string
    currentDebt: number
}) {
    const { hasPermission } = useAuth()
    // Sổ còn mở được từ màn Báo cáo chỉ với reports.read → thao tác thu nợ phải
    // theo quyền riêng, không mời bấm vào thứ server chắc chắn chặn.
    const canPay = hasPermission('customers.payment')
    const canAdjust = hasPermission('customers.debt_adjust')
    const canDelete = hasPermission('customers.debt_delete')
    // M-44: hoàn tiền dư (số dư âm) cho khách — tiền ra khỏi quỹ, quyền riêng.
    const credit = currentDebt < 0 ? -currentDebt : 0
    const canRefund = hasPermission('customers.refund') && credit > 0

    const [type, setType] = useState<DebtTransactionType | ''>('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [page, setPage] = useState(1)
    const [payOpen, setPayOpen] = useState(false)
    const [adjustOpen, setAdjustOpen] = useState(false)
    const [refundOpen, setRefundOpen] = useState(false)
    // receiptId đang mở popup chi tiết chứng từ (undefined = đóng).
    const [receiptId, setReceiptId] = useState<string>()
    // Dòng đang chờ xác nhận xóa (null = dialog đóng) + lý do người dùng nhập.
    const [deleteTarget, setDeleteTarget] = useState<DebtTransaction | null>(
        null,
    )
    const [deleteReason, setDeleteReason] = useState('')
    const deleteMutation = useDeleteDebtTransaction(customerId)

    const handleConfirmDelete = () => {
        if (!deleteTarget) return
        deleteMutation.mutate(
            {
                txnId: deleteTarget.id,
                reason: deleteReason.trim() || undefined,
            },
            {
                onSuccess: () => {
                    setDeleteTarget(null)
                    setDeleteReason('')
                },
            },
        )
    }

    const [exporting, setExporting] = useState(false)

    // Xuất theo ĐÚNG bộ lọc đang chọn (không kèm phân trang) — file tải về phải
    // khớp cái người dùng đang nhìn, lệch là mất tin cậy vào con số.
    const handleExport = async () => {
        setExporting(true)
        try {
            const res = await customerDebtApi.exportTransactions(customerId, {
                type: type || undefined,
                from: from || undefined,
                to: to || undefined,
            })
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? `so-cong-no-${customerId}.xlsx`,
            )
        } catch {
            toast.error(m.customer_debt_export_failed())
        } finally {
            setExporting(false)
        }
    }

    const { data, isLoading } = useCustomerDebtTransactions(customerId, {
        type: type || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        per_page: PER_PAGE,
    })

    const rows = data?.data ?? []
    const meta = data?.meta

    return (
        <div className="space-y-4">
            {/* Header: badge dư nợ + hành động */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                        {m.customer_debt_ledger_current()}
                    </span>
                    <DebtAmount value={currentDebt} withLabel />
                </div>
                <div className="flex gap-2">
                    {canPay && (
                        <Button size="sm" onClick={() => setPayOpen(true)}>
                            {m.customer_debt_ledger_pay_btn()}
                        </Button>
                    )}
                    {canAdjust && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setAdjustOpen(true)}
                        >
                            {m.customer_debt_ledger_adjust_btn()}
                        </Button>
                    )}
                    {canRefund && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRefundOpen(true)}
                        >
                            {m.customer_refund_btn()}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void handleExport()}
                        disabled={exporting}
                    >
                        {exporting ? (
                            <Loader2 className="size-4 animate-spin mr-1.5" />
                        ) : (
                            <Download className="size-4 mr-1.5" />
                        )}
                        {m.customer_debt_export()}
                    </Button>
                </div>
            </div>

            {/* Bộ lọc — mobile xếp dọc full-width, sm+ xếp ngang */}
            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                <div className="w-full sm:w-auto">
                    <Label className="mb-1 block text-xs text-muted-foreground">
                        {m.customer_debt_ledger_col_type()}
                    </Label>
                    <Select
                        value={type || 'all'}
                        onValueChange={(v) => {
                            setType(
                                v === 'all' ? '' : (v as DebtTransactionType),
                            )
                            setPage(1)
                        }}
                    >
                        <SelectTrigger className="w-full sm:w-40">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">
                                {m.customer_debt_filter_all_types()}
                            </SelectItem>
                            {TYPES.map((t) => (
                                <SelectItem key={t} value={t}>
                                    {TYPE_LABEL[t]()}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full sm:w-auto">
                    <Label
                        htmlFor="ledger-from"
                        className="mb-1 block text-xs text-muted-foreground"
                    >
                        {m.warehouse_sales_filter_from()}
                    </Label>
                    <Input
                        id="ledger-from"
                        type="date"
                        value={from}
                        onChange={(e) => {
                            setFrom(e.target.value)
                            setPage(1)
                        }}
                        className="w-full sm:w-40"
                    />
                </div>
                <div className="w-full sm:w-auto">
                    <Label
                        htmlFor="ledger-to"
                        className="mb-1 block text-xs text-muted-foreground"
                    >
                        {m.warehouse_sales_filter_to()}
                    </Label>
                    <Input
                        id="ledger-to"
                        type="date"
                        value={to}
                        onChange={(e) => {
                            setTo(e.target.value)
                            setPage(1)
                        }}
                        className="w-full sm:w-40"
                    />
                </div>
            </div>

            {/* Bảng ledger — desktop bảng, mobile card */}
            {isLoading ? (
                <div className="rounded-md border py-8 text-center text-muted-foreground">
                    {m.common_loading()}
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-md border py-8 text-center text-muted-foreground">
                    {m.customer_debt_ledger_empty()}
                </div>
            ) : (
                <>
                    <div className="hidden rounded-md border md:block">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_date()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_type()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_ref()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_amount()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_balance()}
                                    </TableHead>
                                    <TableHead>
                                        {m.customer_debt_ledger_col_note()}
                                    </TableHead>
                                    {canDelete && <TableHead />}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.id}>
                                        <TableCell className="text-sm">
                                            {formatVN(r.occurred_at)}
                                        </TableCell>
                                        <TableCell>
                                            <DebtTypeBadge type={r.type} />
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            <RefCell
                                                tx={r}
                                                onOpenReceipt={setReceiptId}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <DebtAmount
                                                value={r.amount}
                                                signed
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">
                                            {formatVnd(r.balance_after)}
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">
                                            {r.note ?? '—'}
                                        </TableCell>
                                        {canDelete && (
                                            <TableCell className="text-right">
                                                {isDeletableType(r.type) && (
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        aria-label={m.customer_debt_delete_btn()}
                                                        onClick={() =>
                                                            setDeleteTarget(r)
                                                        }
                                                    >
                                                        <Trash2 className="size-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="space-y-2 md:hidden">
                        {rows.map((r) => (
                            <div
                                key={r.id}
                                className="space-y-2 rounded-md border p-3"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <DebtTypeBadge type={r.type} />
                                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                        {formatVN(r.occurred_at)}
                                        {canDelete &&
                                            isDeletableType(r.type) && (
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="size-7"
                                                    aria-label={m.customer_debt_delete_btn()}
                                                    onClick={() =>
                                                        setDeleteTarget(r)
                                                    }
                                                >
                                                    <Trash2 className="size-4 text-destructive" />
                                                </Button>
                                            )}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-muted-foreground">
                                        {m.customer_debt_ledger_col_amount()}
                                    </span>
                                    <DebtAmount value={r.amount} signed />
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-muted-foreground">
                                        {m.customer_debt_ledger_col_balance()}
                                    </span>
                                    <span className="font-medium">
                                        {formatVnd(r.balance_after)}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs text-muted-foreground">
                                        {m.customer_debt_ledger_col_ref()}
                                    </span>
                                    <span className="font-mono text-xs">
                                        <RefCell
                                            tx={r}
                                            onOpenReceipt={setReceiptId}
                                        />
                                    </span>
                                </div>
                                {r.note ? (
                                    <div className="text-xs text-muted-foreground">
                                        {r.note}
                                    </div>
                                ) : null}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Phân trang */}
            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {page} / {meta.total_pages}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage((p) => p - 1)}
                        >
                            {m.common_prev()}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage((p) => p + 1)}
                        >
                            {m.common_next()}
                        </Button>
                    </div>
                </div>
            )}

            {canPay && (
                <CustomerDebtPaymentDialog
                    customerId={customerId}
                    currentDebt={currentDebt}
                    open={payOpen}
                    onOpenChange={setPayOpen}
                />
            )}
            {canAdjust && (
                <CustomerDebtAdjustmentDialog
                    customerId={customerId}
                    currentDebt={currentDebt}
                    open={adjustOpen}
                    onOpenChange={setAdjustOpen}
                />
            )}

            {canRefund && (
                <CustomerRefundDialog
                    customerId={customerId}
                    credit={credit}
                    open={refundOpen}
                    onOpenChange={setRefundOpen}
                />
            )}

            <CustomerPaymentReceiptDialog
                customerId={customerId}
                receiptId={receiptId}
                open={Boolean(receiptId)}
                onOpenChange={(o) => !o && setReceiptId(undefined)}
            />

            {/* Confirm xóa dòng công nợ. Nút xác nhận CHỈ disable khi đang gửi
                request (chống double-submit), không phải cổng validate. */}
            <Dialog
                open={Boolean(deleteTarget)}
                onOpenChange={(o) => {
                    if (!o) {
                        setDeleteTarget(null)
                        setDeleteReason('')
                    }
                }}
            >
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {m.customer_debt_delete_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {m.customer_debt_delete_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    {deleteTarget && (
                        <div className="flex items-center justify-between gap-2 rounded-md border p-3">
                            <DebtTypeBadge type={deleteTarget.type} />
                            <DebtAmount value={deleteTarget.amount} signed />
                        </div>
                    )}
                    <div>
                        <Label
                            htmlFor="delete-reason"
                            className="mb-1 block text-xs text-muted-foreground"
                        >
                            {m.customer_debt_delete_reason_label()}
                        </Label>
                        <Textarea
                            id="delete-reason"
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            rows={2}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDeleteTarget(null)
                                setDeleteReason('')
                            }}
                        >
                            {m.customer_debt_delete_cancel()}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={deleteMutation.isPending}
                            onClick={handleConfirmDelete}
                        >
                            {deleteMutation.isPending ? (
                                <Loader2 className="size-4 animate-spin mr-1.5" />
                            ) : null}
                            {m.customer_debt_delete_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

// Chứng từ: link tới đơn khi có sales_order_id, ngược lại text thường.
// Dòng "Đầu kỳ" không có chứng từ gốc → hiện "—" thay vì mã kỹ thuật "OPENING".
function RefCell({
    tx,
    onOpenReceipt,
}: {
    tx: DebtTransaction
    onOpenReceipt: (receiptId: string) => void
}) {
    if (tx.type === 'opening') {
        return <span className="text-muted-foreground">—</span>
    }
    if (tx.sales_order_id) {
        return (
            <Link
                to="/orders/$orderId"
                params={{ orderId: tx.sales_order_id }}
                className="text-primary hover:underline"
            >
                {tx.reference_code ?? '—'}
            </Link>
        )
    }
    // Dòng thanh toán: hiện mã phiếu thu (rút gọn), bấm mở popup chi tiết.
    if (tx.receipt_id) {
        return (
            <button
                type="button"
                onClick={() => onOpenReceipt(tx.receipt_id as string)}
                className="font-mono text-xs text-primary hover:underline"
            >
                {formatDocCode(tx.receipt_id)}
            </button>
        )
    }
    return <>{tx.reference_code ?? '—'}</>
}

// Hiển thị số dư/giá trị có màu: dương→đỏ (khách nợ), âm→xanh (trả trước),
// 0→gạch. `signed` prepend '+' cho số dương (dùng cho cột Giá trị ledger);
// `withLabel` thêm nhãn "Khách nợ"/"Trả trước" (dùng cho badge header).
export function DebtAmount({
    value,
    signed = false,
    withLabel = false,
}: {
    value: number
    signed?: boolean
    withLabel?: boolean
}) {
    if (value === 0) {
        return <span className="text-muted-foreground">—</span>
    }
    const positive = value > 0
    const cls = positive
        ? 'text-red-600 font-medium'
        : 'text-green-600 font-medium'
    const prefix = signed && positive ? '+' : ''
    const label = withLabel
        ? positive
            ? ` (${m.customer_debt_ledger_owed()})`
            : ` (${m.customer_debt_ledger_prepaid()})`
        : ''
    return (
        <span className={cls}>
            {prefix}
            {formatVnd(value)}
            {label}
        </span>
    )
}
